<template>
  <!--
    LoadingSpinner.vue — single source of truth for all loading states.
    Use this instead of inline <svg> spinners anywhere in the app.

    Usage — default md, brand:           <LoadingSpinner />
    Usage — small white for buttons:     <LoadingSpinner size="sm" color="white" />
    Usage — large with visible label:    <LoadingSpinner size="lg" show-label />
  -->
  <div
    role="status"
    :aria-label="label"
    class="inline-flex items-center"
    :class="showLabel ? 'flex-col gap-2' : ''"
  >
    <div
      class="rounded-full animate-spin"
      :style="spinnerStyle"
    ></div>
    <span v-if="showLabel" class="text-sm text-ink-500">{{ label }}</span>
    <span v-else class="sr-only">{{ label }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  /** Preset size: sm (16px), md (24px), lg (40px), xl (64px) */
  size: {
    type: [String, Number],
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v) || (typeof v === 'number' && v > 0),
  },
  /** Accent color (the spinning arc) — Tailwind color, e.g. '#8b5cf6' */
  color:      { type: String, default: '#8b5cf6' },
  /** Track color (the faded ring behind) */
  trackColor: { type: String, default: '#e2e8f0' },
  /** Border width in pixels (auto-computed from size if unset) */
  borderWidth: { type: Number, default: undefined },
  /** Accessible label */
  label:      { type: String, default: 'Loading' },
  /** Show the label text visibly below the spinner */
  showLabel:  { type: Boolean, default: false },
});

const sizeMap = { sm: 16, md: 24, lg: 40, xl: 64 };

const pixelSize = computed(() =>
  typeof props.size === 'number' ? props.size : (sizeMap[props.size] || 24)
);

const bw = computed(() => {
  if (props.borderWidth !== undefined) return props.borderWidth;
  const s = pixelSize.value;
  if (s <= 16) return 2;
  if (s <= 24) return 2;
  if (s <= 40) return 3;
  return 4;
});

const spinnerStyle = computed(() => ({
  width: `${pixelSize.value}px`,
  height: `${pixelSize.value}px`,
  borderWidth: `${bw.value}px`,
  borderStyle: 'solid',
  borderColor: props.trackColor,
  borderTopColor: props.color,
}));
</script>
